<script setup lang="tsx">
import { ref, onBeforeMount } from 'vue'
import http from '@/services/http'
import DataAssetFilters from './DataAssetFilters.vue'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useDataAssetColumns from './columns'
import columnStore from '@/stores/column'
import type { DataAsset, PageRequest, PageResponse, Response } from '@/models/api-generated'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import { formatDate, getResponsibleIcon, hasPermission, viewing } from '@/services/utils'
import { fetchCustomValuesForPage, type CustomValueMap } from '@/services/custom'
import CustomRow from '../custom_field/CustomRow.vue'
import CustomHeader from '../custom_field/CustomHeader.vue'
import { dataAssetRefreshCallback } from '@/components/business_processes/shared'
import sliderStore from '@/stores/slider'
import type { DataAssetFilter } from '@/models/filters-generated'
import { riskConfig, getRiskConfig } from '../risk_management/riskConfig'
import { h } from 'vue'
import RisksTooltip from '../risk_management/RisksTooltip.vue'
import { PermOwnRiskRead, PermAllRiskRead } from '@/models/enums-generated'

const props = defineProps({
	component: { default: 'data_assets' },
	disableRowClick: { type: Boolean },
	bpId: { type: Number },
	bpAdd: { type: Boolean },
})

const emits = defineEmits<{
	checked: [id: number]
	unchecked: [id: number]
}>()

const slider = sliderStore()
const model = defineModel<PageResponse<DataAsset>>()
const customValues = ref<CustomValueMap>()
const isLoading = ref(false)
const { columns, custom } = useDataAssetColumns(props.component)
const root = columnStore().defineComponent(props.component, fetch)
dataAssetRefreshCallback.value ??= []
dataAssetRefreshCallback.value.push(root.triggerFetch)

const toggleCheckbox = (assetId: number, checked: boolean) => {
	if (!model.value) return
	const index = model.value.items.findIndex((a) => a.id === assetId)
	if (index !== -1) {
		model.value.items[index].isChecked = checked
	} else {
		const baseAsset = model.value.items.find((a) => a.id === assetId)
		if (baseAsset) model.value.items.push({ ...baseAsset, isChecked: checked })
	}

	if (checked) emits('checked', assetId)
	else emits('unchecked', assetId)
}

const checkAllAssets = (checked: boolean) => {
	const ids = model.value?.items.map((a) => a.id) ?? []

	ids.forEach((id) => {
		toggleCheckbox(id, checked)
	})
}

async function fetch(signal: AbortSignal) {
	const request: PageRequest<DataAssetFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		filter: root.filterValue() as DataAssetFilter,
		orderBy: root.orderValue(),
	}

	if (props.bpId) request.filter.bpId = props.bpId
	if (props.bpAdd) request.filter.bpAdd = props.bpAdd

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<DataAsset>>>(`api/dataasset/list`, request, { signal })
	customValues.value = await fetchCustomValuesForPage('data_assets', data)
	isLoading.value = false

	if (data.data) model.value = data.data
}

function rowClick(asset: DataAsset) {
	slider.openDataAsset(asset.id, asset.name)
}

function goToRisks(names: string[]) {
	if (viewing.value === 0) return
	const params = new URLSearchParams()
	names.forEach((name) => {
		params.append('name', name)
	})
	window.location.href = `/app/risk-management?${params}`
}

onBeforeMount(async () => {
	if (viewing.value === 0) return
	if (hasPermission(PermOwnRiskRead, PermAllRiskRead)) riskConfig.value = await getRiskConfig()
})
</script>

<template>
	<DataAssetFilters :component="props.component"></DataAssetFilters>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th v-if="props.disableRowClick">
							<input class="form-check-input" type="checkbox" @change="checkAllAssets(($event.target as HTMLInputElement).checked)" />
						</th>

						<th v-if="columns.name.value.isVisible">
							<OrderLabel v-model="columns.name.value">{{ $t('misc.name') }}</OrderLabel>
						</th>

						<th v-if="columns.instance.value.isVisible">
							<OrderLabel v-model="columns.instance.value">{{ $t('instance') }}</OrderLabel>
						</th>
						<th v-if="columns.responsible.value.isVisible">
							<OrderLabel v-model="columns.responsible.value">
								{{ $t('responsible') }}
							</OrderLabel>
						</th>

						<th v-if="columns.date.value.isVisible">
							<OrderLabel v-model="columns.date.value">{{ $t('date.date') }}</OrderLabel>
						</th>

						<th v-if="columns.hasPersonalData.value.isVisible">
							<OrderLabel v-model="columns.hasPersonalData.value">{{ $t('hasPersonalData') }}</OrderLabel>
						</th>

						<th v-if="columns.location.value.isVisible">
							<OrderLabel v-model="columns.location.value">{{ $t('location') }}</OrderLabel>
						</th>
						<th v-if="columns.risks.value.isVisible && viewing > 0">{{ columns.risks.value.label }}</th>
						<CustomHeader :columns="custom"></CustomHeader>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(asset, i) in model?.items"
						@mouseup.stop="!props.disableRowClick && rowClick(asset)"
						:key="asset.id"
						class="data-asset-row"
						:class="{ odd: i % 2 != 0 }"
					>
						<td v-if="props.disableRowClick">
							<input
								:id="'data-asset-checkbox' + asset.id"
								class="form-check-input"
								type="checkbox"
								:checked="asset.isChecked"
								@change="toggleCheckbox(asset.id, ($event.target as HTMLInputElement).checked)"
							/>
						</td>
						<td v-if="columns.name.value.isVisible">
							{{ asset.name }}
						</td>
						<td v-if="columns.instance.value.isVisible">
							{{ asset.instance.name }}
						</td>
						<td v-if="columns.responsible.value.isVisible">
							<i v-if="asset.responsible" :class="getResponsibleIcon(asset.responsible)"></i>
							{{ asset.responsible ? asset.responsible.label : '-' }}
						</td>
						<td v-if="columns.date.value.isVisible">
							{{ asset.date ? formatDate(asset.date) : '-' }}
						</td>
						<td v-if="columns.hasPersonalData.value.isVisible" class="text-center">
							<i v-if="asset.hasPersonalData" class="bi bi-circle-fill status-normal-background"></i>
							<i v-else class="bi bi-circle-fill status-verybig-background"></i>
						</td>
						<td v-if="columns.location.value.isVisible">
							{{ asset.location }}
						</td>
						<td v-if="columns.risks.value.isVisible && viewing > 0" class="text-center">
							<span
								class="risks-cell"
								v-one-tooltip="{
									content: () => h(RisksTooltip, { risks: asset.risks }),
								}"
								@click.stop="goToRisks(asset.risks.map((r) => r.name))"
							>
								{{ asset.risks ? asset.risks.length : 0 }}
							</span>
						</td>
						<CustomRow :columns="custom" v-model="customValues" :row-id="asset.id"></CustomRow>
					</tr>
				</tbody>
			</table>
		</div>
	</LoadingContainer>
	<Pagination v-model="root.pagination" :total-pages="model?.pages" :tableTitle="props.component" />
</template>

<style lang="sass" scoped>
td:hover
    cursor: pointer
</style>
